<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import { gameGet } from "@/common/api/rest/game/game";

import type { GameType } from "@/common/types/game/game";

import TabGameInfos from "@/components/tabs/game/infos.vue";
import TabGameDetails from "@/components/tabs/game/details.vue";
import TabGameImages from "@/components/tabs/game/images.vue";
import TabGameName from "@/components/tabs/game/name.vue";
import TabGameSlug from "@/components/tabs/game/slug.vue";
import TabGameConsole from "@/components/tabs/game/console.vue";
import TabGameRelated from "@/components/tabs/game/related.vue";

import { breadcrumbSimple, iconMenu, svgIcon } from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const selectedTab = ref("infos");
const gameData = ref<GameType | null>(null);

const gameId = computed(() => route.params.item as string);

const breadcrumbList = computed(() => [
  {
    key: "collections",
    label: t("common.labels.collections"),
    url: `/collection`,
  },
  {
    key: "collection",
    label: t("collection.labels.games"),
    url: `/collection/game`,
  },
  {
    key: "edit",
    label: gameData.value?.name ?? "...",
  },
]);

const menuList = computed(() => [
  {
    key: "infos",
    icon: "field",
    label: t("collection.labels.infos"),
    url: `/collection/game/${gameId.value}/infos`,
  },
  {
    key: "name",
    icon: "no-image",
    label: t("common.labels.other_names"),
    url: `/collection/game/${gameId.value}/name`,
  },
  {
    key: "slug",
    icon: "no-image",
    label: t("slug.labels.slugs"),
    url: `/collection/game/${gameId.value}/slug`,
  },
  {
    key: "image",
    icon: "no-image",
    label: t("collection.labels.images"),
    url: `/collection/game/${gameId.value}/image`,
  },
  {
    key: "details",
    icon: "no-image",
    label: t("collection.labels.details"),
    url: `/collection/game/${gameId.value}/details`,
  },
  {
    key: "console",
    icon: "no-image",
    label: t("collection.labels.consoles"),
    url: `/collection/game/${gameId.value}/console`,
  },
  {
    key: "related",
    icon: "game",
    label: t("collection.labels.related"),
    url: `/collection/game/${gameId.value}/related`,
  },
]);

function back() {
  router.push(`/collection/game`);
}

async function changeMenu(url: string) {
  await fetchData();
  router.push(url);
}

// game
async function fetchData() {
  await gameGet(gameId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        gameData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => route.params.tab,
  () => {
    selectedTab.value = route.params.tab as string;
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  await fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="gamepad" size="2rem" />
          EDITAR GAME
        </div>

        <div class="actions">
          <button class="btn btn-default w-8 h-8" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <icon-menu
          :tabs="menuList"
          :active="selectedTab"
          size="3rem"
          @selected="changeMenu($event)"
        />

        <template v-if="gameData">
          <template v-if="selectedTab === 'infos'">
            <tab-game-infos
              :game-id="gameId"
              :game="gameData"
              @update:data="fetchData()"
            />
          </template>

          <template v-if="selectedTab === 'details'">
            <tab-game-details :game-id="gameId" />
          </template>

          <template v-if="selectedTab === 'name'">
            <tab-game-name :game-id="gameId" />
          </template>

          <template v-if="selectedTab === 'slug'">
            <tab-game-slug :owner-id="gameId" />
          </template>

          <template v-if="selectedTab === 'console'">
            <tab-game-console :owner-id="gameId" />
          </template>

          <template v-if="selectedTab === 'related'">
            <tab-game-related :game-id="gameId" />
          </template>

          <template v-if="selectedTab === 'image'">
            <tab-game-images :game-id="gameId" />
          </template>
        </template>
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
